// 客戶資料庫: 獨立 Profile / 多地址 / 報價歷史
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, money, fmtDate, QT_STATUS } from '../api.js';
import { Modal, Field, StatusBadge, Loading, Empty, PageHead, useToast, useConfirm } from '../ui.jsx';
import CustomerEditor from '../CustomerEditor.jsx';

function CustomerDetail({ id, onClose, onEdit, refresh }) {
  const [c, setC] = useState(null);
  const [qts, setQts] = useState(null);
  useEffect(() => {
    api.get(`/customers/${id}`).then(setC);
    api.get(`/customers/${id}/quotations`).then(setQts);
  }, [id]);
  if (!c) return null;
  return (
    <Modal size="lg" onClose={onClose} icon="🏢" title={`👤 ${c.name}`}
      footer={<>
        <button className="btn" onClick={onClose}>關閉</button>
        <button className="btn primary" onClick={() => onEdit(c)}>✏️ 編輯</button>
      </>}>
      <div className="kv mb">
        <div>編號</div><div>{c.code}</div>
        <div>聯絡人</div><div>{c.contact || '—'}</div>
        <div>電話</div><div>{c.phone || '—'}</div>
        <div>Email</div><div>{c.email || '—'}</div>
        <div>備註</div><div style={{ whiteSpace: 'pre-line' }}>{c.notes || '—'}</div>
      </div>
      <div className="card-title"><span className="ico-badge sky" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📍</span>地址 ({c.addresses.length})</div>
      {c.addresses.map(a => (
        <div key={a.id} className="row mb" style={{ background: 'var(--surface-soft)', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--line)', fontSize: 13 }}>
          <strong>{a.label || '地址'}</strong>{a.is_default ? <span className="st st-blue">預設</span> : null}
          <span style={{ color: 'var(--ink-2)' }}>{a.address}</span>
        </div>
      ))}
      <div className="card-title mt"><span className="ico-badge" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📄</span>報價歷史 ({(qts || []).length})</div>
      {(qts === null) ? <Loading /> : qts.length === 0 ? <Empty icon="📄" text="未有報價紀錄" /> : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>編號</th><th>日期</th><th>項目</th><th className="num">金額</th><th>狀態</th></tr></thead>
            <tbody>
              {qts.map(q => (
                <tr key={q.id}>
                  <td className="strong">{q.quote_no}</td>
                  <td>{fmtDate(q.issue_date)}</td>
                  <td>{q.title || '—'}</td>
                  <td className="num strong">{money(q.total)}</td>
                  <td><StatusBadge map={QT_STATUS} value={q.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function Customers() {
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState(null); // null | {id?}
  const [detailId, setDetailId] = useState(null);
  const refresh = () => api.get('/customers').then(setRows);
  useEffect(() => { refresh(); }, []);

  const del = (c) => confirm(`確定刪除客戶「${c.name}」？`, async () => {
    try { await api.del(`/customers/${c.id}`); toast('已刪除'); refresh(); }
    catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '刪除' });

  const filtered = (rows || []).filter(c => !q || c.name.includes(q) || (c.code || '').toLowerCase().includes(q.toLowerCase()) || (c.contact || '').includes(q));
  const totalAR = (rows || []).reduce((s, c) => s + (c.outstanding || 0), 0);

  return (
    <div>
      <PageHead icon="🏢" title="客戶資料庫" desc={`獨立客戶 Profile · 多地址管理 · 報價歷史 — 總應收未收: HK$ ${money(totalAR)}`}
        actions={<>
          <div className="search-box"><input className="input" placeholder="搜尋客戶…" value={q} onChange={e => setQ(e.target.value)} /></div>
          <button className="btn primary" onClick={() => setEditor({})}>➕ 新增客戶</button>
        </>} />
      <div className="card">
        {rows === null ? <Loading /> : filtered.length === 0 ? <Empty icon="🏢" text="沒有客戶" /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>客戶</th><th>聯絡人</th><th>電話</th><th className="num">地址</th><th className="num">報價</th><th className="num">訂單</th><th className="num">未收數</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="clickable" onClick={() => setDetailId(c.id)}>
                    <td>
                      <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                        <span className="avatar">{c.name.slice(0, 1)}</span>
                        <div><div className="strong">{c.name}</div><div className="sub">{c.code}</div></div>
                      </div>
                    </td>
                    <td>{c.contact || '—'}</td>
                    <td>{c.phone || '—'}</td>
                    <td className="num">{c.address_count}</td>
                    <td className="num">{c.quotation_count}</td>
                    <td className="num">{c.order_count}</td>
                    <td className="num strong">{money(c.outstanding)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <button className="btn sm" onClick={() => setEditor({ id: c.id })}>✏️</button>
                        <button className="btn sm coral" onClick={() => del(c)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editor && <CustomerLoader editor={editor} rows={rows}
        onClose={() => setEditor(null)}
        onEdit={(c) => { setEditor(null); setTimeout(() => setEditor({ id: c.id }), 0); }}
        onSaved={() => { setEditor(null); refresh(); }} />}
      {editor === null && detailId && (
        <CustomerDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(c) => { setDetailId(null); setTimeout(() => setEditor({ id: c.id }), 0); }} refresh={refresh} />
      )}
      {confirmNode}
    </div>
  );
}

// 編輯前先載入完整客戶資料 (連地址) — 免得地址清單空白, 儲存時誤刪地址
function CustomerLoader({ editor, rows, onClose, onEdit, onSaved }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    if (!editor.id) { setFull({}); return; }
    api.get('/customers/' + editor.id).then(setFull).catch(() => setFull({}));
  }, [editor.id]);
  if (!full) return null;
  return <CustomerEditor initial={editor.id ? full : {}} onClose={onClose} onEdit={onEdit} onSaved={onSaved} />;
}
